'use client';
import React, { useState, useEffect } from 'react';
import { Clock, AlertTriangle, Loader2 } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { createClient } from '@/lib/supabase/client';
import { wormBedService, feedingService, type WormBed, type FeedingLog } from '@/lib/services/farmService';

interface BedDisplayItem {
  id: string;
  name: string;
  status: 'fed' | 'needs-feeding' | 'harvest-ready' | 'warning' | 'active' | 'resting' | 'inactive';
  lastFed: string;
  alert: boolean;
}

function computeStatus(bed: WormBed, lastLog?: FeedingLog): BedDisplayItem['status'] {
  if (!bed.isActive) return 'inactive';
  if (!lastLog) return 'needs-feeding';
  const hoursAgo = (Date.now() - new Date(lastLog.createdAt).getTime()) / 3600000;
  if (hoursAgo < 12) return 'fed';
  if (hoursAgo < 24) return 'active';
  if (hoursAgo < 36) return 'harvest-ready';
  if (hoursAgo < 48) return 'resting';
  return 'needs-feeding';
}

function formatLastFed(lastLog?: FeedingLog): string {
  if (!lastLog) return 'Never';
  const diff = Date.now() - new Date(lastLog.createdAt).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function BedStatusGrid() {
  const [bedItems, setBedItems] = useState<BedDisplayItem[]>([]);
  const [loading, setLoading] = useState(true);

  const loadBeds = async () => {
    try {
      const [beds, logs] = await Promise.all([
        wormBedService.getAll(),
        feedingService.getAll(),
      ]);

      const items: BedDisplayItem[] = beds.map((bed) => {
        const bedLogs = logs.filter((l) => l.bedId === bed.bedName || l.bedId === bed.id);
        const lastLog = bedLogs[0]; // already sorted desc
        const status = computeStatus(bed, lastLog);
        const alert = status === 'needs-feeding' || status === 'warning';
        return {
          id: bed.id,
          name: bed.bedName,
          status,
          lastFed: formatLastFed(lastLog),
          alert,
        };
      });

      setBedItems(items);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBeds();
  }, []);

  // Real-time subscriptions
  useEffect(() => {
    const supabase = createClient();

    const bedsChannel = supabase
      .channel('rt-bed-status-grid-beds')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'worm_beds' }, loadBeds)
      .subscribe();

    const logsChannel = supabase
      .channel('rt-bed-status-grid-logs')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'feeding_logs' }, loadBeds)
      .subscribe();

    return () => {
      supabase.removeChannel(bedsChannel);
      supabase.removeChannel(logsChannel);
    };
  }, []);

  if (loading) {
    return (
      <div>
        <h2 className="text-base font-semibold text-foreground mb-3">Worm Bed Status</h2>
        <div className="flex items-center justify-center py-8">
          <Loader2 size={22} className="animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-base font-semibold text-foreground mb-3">Worm Bed Status</h2>
      {bedItems.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">No beds configured yet</p>
      ) : (
        <div className="grid grid-cols-2 gap-2.5">
          {bedItems.map((bed) => (
            <div
              key={bed.id}
              className={`bg-card rounded-xl border p-3 shadow-sm ${
                bed.alert ? 'border-warning/40' : 'border-border'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-foreground">{bed.name}</span>
                {bed.alert && <AlertTriangle size={13} className="text-warning" />}
              </div>
              <StatusBadge variant={bed.status} />
              <div className="flex items-center gap-1 mt-2">
                <Clock size={11} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Fed {bed.lastFed}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}